import React from 'react';
import { FileText, RotateCcw } from 'lucide-react';

import type {
  AssumptionSource,
  GenerationAssumption,
} from '../../../types/generationImpact';

const sourceLabels: Record<AssumptionSource, string> = {
  'site-evidence': 'SED / site',
  document: 'Document',
  dam: 'DAM',
  'project-intent': 'Project Intent',
  vendor: 'Equipment',
  user: 'User',
  'planning-default': 'Planning default',
};

function testIdField(field: string): string {
  return field.replaceAll(/[^a-zA-Z0-9_-]/g, '-');
}

function evidenceTitle(assumption: GenerationAssumption): string {
  const detail = assumption.evidenceRefs?.map((ref) => {
    if (ref.kind === 'document') {
      return `Document ${ref.documentId}${ref.page == null ? '' : ` · page ${ref.page}`} · SHA-256 ${ref.documentSha256}`;
    }
    if (ref.kind === 'dam') {
      return `${ref.reportType} DAM ${ref.reportVersion}`
        + `${ref.basis ? ` · ${ref.basis}` : ''}`
        + `${ref.vintage ? ` · vintage ${ref.vintage}` : ''}`
        + `${ref.conversionFormula ? ` · ${ref.conversionFormula}` : ''}`;
    }
    return `SED ${ref.section}.${ref.fieldPath}`
      + `${ref.sourceVersion ? ` · ${ref.sourceVersion}` : ''}`;
  }).join('\n');
  return [assumption.label, assumption.citation, detail].filter(Boolean).join('\n');
}

export interface GenerationScreeningSourceBadgeProps {
  field: string;
  assumption?: GenerationAssumption | undefined;
  onOpenDocument?: ((documentId: string) => void) | undefined;
  onUseLatest?: (() => void) | undefined;
  latestLabel?: string | undefined;
}

/**
 * Compact field-level lineage. It deliberately renders next to the input
 * rather than in a detached ledger so operators can see which authority owns
 * the exact number they are about to use.
 */
export function GenerationScreeningSourceBadge({
  field,
  assumption,
  onOpenDocument,
  onUseLatest,
  latestLabel,
}: GenerationScreeningSourceBadgeProps): React.JSX.Element {
  const source = assumption?.source ?? 'planning-default';
  const documentRef = assumption?.evidenceRefs?.find(ref => ref.kind === 'document');
  const title = assumption ? evidenceTitle(assumption) : 'Platform planning catalog fallback.';
  return (
    <span
      className="mt-1 flex min-w-0 flex-wrap items-center gap-1 text-[9px] normal-case tracking-normal"
      data-testid={`generation-screening-source-${testIdField(field)}`}
    >
      {documentRef && onOpenDocument ? (
        <button
          type="button"
          className="inline-flex max-w-full items-center gap-1 border border-primary/40 bg-primary/5 px-1.5 py-0.5 font-bold text-primary hover:bg-primary/10 rounded-md"
          onClick={() => onOpenDocument(documentRef.documentId)}
          title={title}
        >
          <FileText className="h-3 w-3 shrink-0" aria-hidden />
          <span className="truncate">
            {sourceLabels[source]}{documentRef.page == null ? '' : ` · p.${documentRef.page}`}
          </span>
        </button>
      ) : (
        <span
          className="inline-flex max-w-full items-center border border-border bg-background px-1.5 py-0.5 font-bold text-text-secondary rounded-md"
          title={title}
        >
          <span className="truncate">{sourceLabels[source]}</span>
        </span>
      )}
      {assumption?.confidence && (
        <span className="uppercase text-text-muted">{assumption.confidence}</span>
      )}
      {onUseLatest && (
        <button
          type="button"
          className="inline-flex items-center gap-1 font-bold text-primary hover:underline"
          onClick={onUseLatest}
          title={latestLabel ?? 'Replace the manual value with the latest eligible evidence.'}
        >
          <RotateCcw className="h-3 w-3" aria-hidden />
          Use latest evidence
        </button>
      )}
    </span>
  );
}
